/**
 * Personagem: como um personagem do Mistik Island é montado.
 *
 * <p>Três camadas, na ordem em que dependem umas das outras: os <b>atributos</b> que se
 * distribuem, os <b>secundários</b> que caem deles por uma fórmula, e as <b>perícias</b>
 * com a rolagem de cada uma. Quem chega no `FOR + PER + d20` sem saber o que é FOR está
 * lendo na ordem errada.
 *
 * <p>As perícias deixaram de ser cartão de leitura e viraram a <b>linha de rolagem</b>
 * que a tela de Combate tinha. O motivo é que a conta é do personagem: ela usa os
 * atributos que estão logo acima, e mantê-la noutra tela obrigava a ir e voltar para
 * montar uma fórmula. A tela de Combate ficou com o que só ela responde — etapas, ações
 * e distância.
 *
 * <p>Tudo aqui é <b>rascunho</b>, mas rascunho <b>do time</b>: ele é gravado no GG Pilot
 * e fica visível para todos. Não há campo próprio para nada disto — o documento é
 * guardado inteiro, e inventar um esquema seria decidir por fora uma questão de regras.
 * Quando fecharem, viram tabela.
 */
import { TituloDaPagina } from '../../shell/TituloDaPagina';
import { ATRIBUTOS, LINHAS_DE_BONUS } from './atributos';
import {
  DERIVADOS,
  CHAVES_DE_FORMULA,
  chaveDaFormula,
  corDoDerivado,
  escritas,
  formulasDoNavegador,
  formulasEmBranco,
  interpretarFormulas,
} from './derivados';
import type { Formulas } from './derivados';
import { AtributoIcone, DerivadoIcone } from './IconesDaFicha';
import { LinhaDeRolagem } from './LinhaDeRolagem';
import { PericiaIcone } from '../mundo/PericiaIcone';
import {
  COLETAS,
  COMBATE_DEFENSIVAS,
  COMBATE_OFENSIVAS,
  CRIACOES,
  corDe,
  periciaPorId,
} from './pericias';
import type { Pericia } from './pericias';
import { AvisoDeGravacao, PAGINAS, useRascunho } from './rascunhoDeGameDesign';
import { interpretarRolagens, montadas, rolagensDoNavegador, rolagensEmBranco } from './rolagens';
import './ficha.css';

const CHAVE_BONUS = 'gg-pilot:ficha:bonus';

/**
 * O que cada perícia é, numa linha.
 *
 * <p>Sobrou do cartão que a linha de rolagem substituiu, e virou o `title` da linha: o
 * texto não cabe mais na tela, mas jogá-lo fora apagaria a única definição escrita de
 * cada perícia que o projeto tem.
 *
 * <p>Um mapa por id, e não um array paralelo à lista: array paralelo depende da ordem, e
 * a ordem muda toda vez que o time acrescenta uma perícia — o texto da Espada acabaria
 * embaixo do Machado sem ninguém notar.
 */
const PAPEL: Record<string, string> = {
  desarmado: 'Punho, cotovelo, joelho. Nunca se está sem ela.',
  espada: 'Corte e estocada. O equilíbrio entre alcance e cadência.',
  machado: 'Peso que abre guarda. Lento, e caro de errar.',
  haste: 'Lança e alabarda: acerta antes de ser alcançado.',
  maca: 'Impacto que ignora armadura em vez de perfurá-la.',
  magiaOfensiva: 'O dano que não vem do braço: fagulha, raio, praga.',
  arco: 'Distância e silêncio. Pede espaço e mãos livres.',
  besta: 'Força emprestada: mira fácil, recarga lenta.',
  esquiva: 'Não estar onde o golpe chega.',
  bloqueio: 'Estar, e aguentar.',
  arcana: 'A magia que apara: selo, barreira, dissipação.',
  herbalismo: 'Do que cresce: ervas, fungos, flores.',
  mineracao: 'Da pedra: minério e gema bruta.',
  esfolagem: 'Do que foi abatido: couro, escama, osso.',
  alquimia: 'Poções, venenos e elixires.',
  forja: 'Armas, ferramentas e armadura pesada.',
  couraria: 'Armadura leve, bolsas e acessórios.',
  tecnomagia: 'Metal e feitiço no mesmo objeto.',
};

/** Bônus por atributo: a sigla aponta para as seis linhas escritas. */
type Bonus = Record<string, string[]>;

const vazio = (): Bonus =>
  Object.fromEntries(ATRIBUTOS.map((a) => [a.sigla, Array(LINHAS_DE_BONUS).fill('')]));

const TODAS_AS_DA_FICHA = [
  ...COMBATE_OFENSIVAS,
  ...COMBATE_DEFENSIVAS,
  ...COLETAS,
  ...CRIACOES,
];

/**
 * Reconstrói os bônus a partir do padrão: assim uma sigla nova, ou uma linha a mais,
 * não quebra o que já estava escrito.
 */
function interpretarBonus(guardado: unknown): Bonus {
  const base = vazio();
  if (!guardado || typeof guardado !== 'object') return base;
  const lido = guardado as Record<string, unknown>;
  for (const a of ATRIBUTOS) {
    const linhas = Array.isArray(lido[a.sigla]) ? (lido[a.sigla] as unknown[]) : [];
    base[a.sigla] = base[a.sigla].map((_, i) =>
      typeof linhas[i] === 'string' ? (linhas[i] as string) : ''
    );
  }
  return base;
}

/** O documento da página `personagem`: o que só esta tela escreve. */
interface Pessoal {
  bonus: Bonus;
  formulas: Formulas;
}

const interpretarPessoal = (guardado: unknown): Pessoal => {
  const doc = (guardado ?? {}) as Record<string, unknown>;
  return {
    bonus: interpretarBonus(doc.bonus),
    formulas: interpretarFormulas(doc.formulas),
  };
};

/** O que ficou no navegador, para a migração de uma vez só. */
function semearPessoal(): Pessoal | undefined {
  let bonusCru: string | null = null;
  try {
    bonusCru = localStorage.getItem(CHAVE_BONUS);
  } catch {
    bonusCru = null;
  }
  const formulas = formulasDoNavegador();
  if (bonusCru == null && !formulas) return undefined;

  return {
    bonus: interpretarBonus(bonusCru ? seguro(bonusCru) : null),
    formulas: formulas ?? formulasEmBranco(),
  };
}

const seguro = (cru: string): unknown => {
  try {
    return JSON.parse(cru);
  } catch {
    return null;
  }
};

export function Ficha() {
  /*
   * Dois documentos, e não um: as rolagens são as MESMAS que a Tier List edita, então
   * elas têm página própria. Guardá-las dentro de `personagem` daria duas cópias da
   * mesma régua — o defeito que estas telas já pagaram uma vez.
   */
  const pessoal = useRascunho(PAGINAS.personagem, interpretarPessoal, semearPessoal);
  const doc = useRascunho(PAGINAS.rolagens, interpretarRolagens, rolagensDoNavegador);

  const bonus = pessoal.valor?.bonus ?? vazio();
  const formulas = pessoal.valor?.formulas ?? formulasEmBranco();
  const rolagens = doc.valor ?? rolagensEmBranco();

  const setBonus = (muda: (antes: Bonus) => Bonus) =>
    pessoal.definir((p) => ({ ...p, bonus: muda(p.bonus) }));
  const setFormulas = (muda: (antes: Formulas) => Formulas) =>
    pessoal.definir((p) => ({ ...p, formulas: muda(p.formulas) }));
  const setRolagens = doc.definir;

  /*
   * Os dois estados viram um: quem está lendo quer saber se o que digitou ficou
   * gravado, e não em qual das duas páginas. Gravando manda enquanto qualquer uma
   * estiver no ar; o erro de qualquer uma aparece, porque um erro escondido é o que faz
   * alguém fechar a aba confiante.
   */
  const gravacao = {
    gravando: pessoal.estado.gravando || doc.estado.gravando,
    erroAoGravar: pessoal.estado.erroAoGravar ?? doc.estado.erroAoGravar,
    atualizadoPor: pessoal.estado.atualizadoPor ?? doc.estado.atualizadoPor,
  };
  const erroAoLer = pessoal.erroAoLer ?? doc.erroAoLer;

  const escrever = (sigla: string, i: number, valor: string) =>
    setBonus((atual) => ({
      ...atual,
      [sigla]: atual[sigla].map((linha, j) => (j === i ? valor : linha)),
    }));

  const preenchidas = Object.values(bonus).flat().filter((l) => l.trim()).length;
  const total = ATRIBUTOS.length * LINHAS_DE_BONUS;

  const formulasEscritas = escritas(formulas);
  const contaDeRolagens = montadas(rolagens, TODAS_AS_DA_FICHA);

  const grupoDeRolagens = (pericias: Pericia[]) => (
    <div className="rl-lista">
      {pericias.map((p) => (
        <LinhaDeRolagem
          key={p.id}
          pericia={p}
          equacao={rolagens[p.id]}
          titulo={PAPEL[p.id]}
          aoMudar={(campo, valor) =>
            setRolagens((atual) => ({ ...atual, [p.id]: { ...atual[p.id], [campo]: valor } }))
          }
        />
      ))}
    </div>
  );

  return (
    <div className="fi">
      <TituloDaPagina>
        Como um personagem é montado: os atributos, o que sai deles por conta, e a rolagem
        de cada perícia.
      </TituloDaPagina>

      {/*
        Um aviso só para os dois documentos da tela. Dois avisos lado a lado — um para a
        página `personagem`, outro para `rolagens` — diriam "Gravando…" em momentos
        diferentes pela mesma digitação, e quem lê não tem por que saber que a tela
        escreve em dois lugares.
      */}
      {erroAoLer ? (
        <p className="gd-gravacao erro">
          {erroAoLer instanceof Error ? erroAoLer.message : 'Não foi possível abrir.'}
        </p>
      ) : (
        <AvisoDeGravacao estado={gravacao} />
      )}

      <section className="fi-secao">
        <h2>A criação do personagem</h2>
        <p className="fi-p">
          Um personagem do Mistik Island é a soma de <b>o que ele é</b> e <b>o que ele
          sabe fazer</b>. Os seis atributos são o corpo — não mudam de uma hora para
          outra, e limitam o teto de tudo o mais. As perícias são o ofício: sobem com
          uso, e é nelas que o jogador escolhe quem quer ser.
        </p>
        <p className="fi-p">
          Entre os dois ficam os <b>secundários</b>: HP, iniciativa, defesa. Ninguém os
          distribui — eles caem de uma fórmula sobre os atributos, e escrever essas
          fórmulas é escrever o equilíbrio do jogo.
        </p>
      </section>

      {/* ---------------------------------------------------------- atributos */}
      <section className="fi-secao">
        <h2>Os seis atributos</h2>
        <p className="fi-nota">
          Cada atributo tem <b>{LINHAS_DE_BONUS} linhas</b> para escrever o que ele
          acrescenta. O número é fixo para a grade ser comparável: dá para ler na
          diagonal o que cada atributo dá no mesmo degrau, e um campo em branco é uma
          pergunta em aberto, visível.
          {preenchidas > 0 && (
            <>
              {' '}
              <b>
                {preenchidas} de {total} escritas.
              </b>
            </>
          )}{' '}
          Rascunho do time, gravado no GG Pilot.
        </p>

        <div className="fi-atributos">
          {ATRIBUTOS.map((a) => {
            const linhas = bonus[a.sigla];
            const feitas = linhas.filter((l) => l.trim()).length;
            return (
              <article
                key={a.sigla}
                className="fi-atributo"
                style={{ '--c': a.cor } as React.CSSProperties}
              >
                <header>
                  <span className="fi-brasao">
                    <AtributoIcone nome={a.sigla} cor={a.cor} tamanho={22} />
                    <b>{a.sigla}</b>
                  </span>
                  <div>
                    <b>{a.nome}</b>
                    <span className="fi-sobre">{a.sobre}</span>
                  </div>
                  {/*
                    A trilha de cada atributo, em miniatura no cabeçalho: seis marcas que
                    acendem conforme se escreve. É o que dá para ler de longe — sem ela, a
                    grade só responde "quanto falta?" a quem percorrer 36 campos.
                  */}
                  <span
                    className="fi-trilha"
                    title={`${feitas} de ${LINHAS_DE_BONUS} escritas`}
                    aria-label={`${a.nome}: ${feitas} de ${LINHAS_DE_BONUS} escritas`}
                  >
                    {linhas.map((linha, i) => (
                      <i key={i} className={linha.trim() ? 'on' : undefined} />
                    ))}
                  </span>
                </header>

                <ol className="fi-bonus">
                  {linhas.map((linha, i) => (
                    <li key={i} className={linha.trim() ? 'escrita' : undefined}>
                      <span className="fi-bonus-n">{i + 1}</span>
                      <input
                        value={linha}
                        onChange={(e) => escrever(a.sigla, i, e.target.value)}
                        placeholder="o que este ponto acrescenta"
                        aria-label={`Bônus ${i + 1} de ${a.nome}`}
                      />
                    </li>
                  ))}
                </ol>
              </article>
            );
          })}
        </div>
      </section>

      {/* -------------------------------------------------- atributos secundários */}
      <section className="fi-secao">
        <h2>Os atributos secundários</h2>
        <p className="fi-p">
          Estes não se distribuem: <b>saem de uma conta</b> sobre os atributos, o nível e
          o equipamento. É por isso que a tela não os deixa marcar — deixa escrever a
          fórmula.
        </p>
        <p className="fi-nota">
          A fórmula é <b>texto livre</b>, e de propósito: um construtor de expressão
          obrigaria a fechar antes a gramática (se multiplica, se o nível entra, se
          arredonda para baixo), e essas são justamente as perguntas em aberto. A dica em
          cinza é <b>sugestão de formato</b>, nunca valor — campo pré-preenchido se lê
          como decisão tomada.
          {formulasEscritas > 0 && (
            <>
              {' '}
              <b>
                {formulasEscritas} de {CHAVES_DE_FORMULA.length} escritas.
              </b>
            </>
          )}{' '}
          Rascunho do time, gravado no GG Pilot. A lista <b>não está fechada</b> —
          cresce conforme o combate for sendo escrito.
        </p>

        {DERIVADOS.map((grupo) => (
          <div
            key={grupo.id}
            className="fi-derivados"
            style={{ '--g': grupo.cor } as React.CSSProperties}
          >
            <div className="fi-derivado-cabeca">
              <span className="fi-grupo-tit">{grupo.titulo}</span>
              <i className="fi-derivado-fio" />
            </div>
            <p className="fi-derivado-sobre">{grupo.sobre}</p>

            <div className="fi-derivado-lista">
              {grupo.itens.map((d) => {
                const cor = corDoDerivado(d, grupo, (id) => {
                  const p = periciaPorId(id);
                  return p && corDe(p);
                });
                const escrita = Boolean((formulas[d.id] ?? '').trim());
                return (
                  <div
                    key={d.id}
                    className={`fi-derivado${escrita ? ' escrita' : ''}`}
                    style={{ '--c': cor } as React.CSSProperties}
                  >
                    <span className="fi-derivado-selo">
                      {/*
                        Esquiva e Bloqueio mostram o ícone da PERÍCIA, não um desenho
                        próprio: elas usam a perícia de mesmo nome, e inventar um segundo
                        símbolo para o mesmo conceito negaria a ligação que a linha declara.
                      */}
                      {d.usaPericia ? (
                        <PericiaIcone pericia={d.usaPericia} cor={cor} tamanho={20} />
                      ) : (
                        <DerivadoIcone nome={d.id} cor={cor} tamanho={20} />
                      )}
                    </span>

                    <span className="fi-derivado-nome">
                      <b>{d.nome}</b>
                      <span>{d.sobre}</span>
                      {/*
                        A perícia que entra na conta. Esquiva e Bloqueio aparecem duas
                        vezes no sistema — perícia que se treina, número que se consulta —,
                        e o segundo se calcula do primeiro. Dizer "usa a perícia Esquiva" é
                        o que impede alguém de ler como duplicação e apagar um dos dois,
                        quebrando a conta do outro.
                      */}
                      <PericiaQueEntra id={d.usaPericia} />
                    </span>

                    <span className="fi-derivado-igual">=</span>

                    {/*
                      Sem variantes, uma fórmula. Com elas, uma por tipo de ataque, em
                      coluna e etiquetadas — é a mesma grandeza medida três vezes, e
                      quebrá-la em três linhas diria que são três secundários.
                    */}
                    {d.variantes ? (
                      <span className="fi-derivado-variantes">
                        {d.variantes.map((v) => {
                          const chave = chaveDaFormula(d, v);
                          return (
                            <span key={v.id} className="fi-variante">
                              <i>{v.rotulo}</i>
                              <input
                                className="fi-derivado-formula"
                                value={formulas[chave] ?? ''}
                                placeholder={d.exemplo}
                                aria-label={`Fórmula de ${d.nome} ${v.rotulo}`}
                                onChange={(e) =>
                                  setFormulas((atual) => ({ ...atual, [chave]: e.target.value }))
                                }
                              />
                            </span>
                          );
                        })}
                      </span>
                    ) : (
                      <input
                        className="fi-derivado-formula"
                        value={formulas[d.id] ?? ''}
                        placeholder={d.exemplo}
                        aria-label={`Fórmula de ${d.nome}`}
                        onChange={(e) =>
                          setFormulas((atual) => ({ ...atual, [d.id]: e.target.value }))
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* ------------------------------------------------------------ perícias */}
      <section className="fi-secao">
        <h2>As perícias e suas rolagens</h2>
        <p className="fi-p">
          Três categorias: <b>Combate</b> decide a luta, <b>Coleta</b> tira matéria-prima
          do mundo e <b>Criação</b> a transforma. Herbalismo, Mineração e Esfolamento
          alimentam Alquimia, Forja e Couraria — uma cada. <b>Tecnomagia é a exceção</b>:
          não tem coleta própria, e é a primeira criação que não nasce de um par.
        </p>
        <p className="fi-p">
          Toda ação que pode falhar soma <b>dois atributos + a perícia + um dado</b>. O
          que muda entre elas é <i>quais</i> dois atributos — minerar é força e vigor,
          herbalismo é percepção e mente —, e é essa escolha que distingue uma Espada de
          um Arco antes mesmo de haver regra escrita.
        </p>
        <p className="fi-nota">
          A lista <b>não está fechada</b> — cresce conforme o jogo cresce. Por isso nenhum
          título aqui diz quantas são. As <b>em cinza</b> já estão desenhadas, mas só
          entram <b>depois da Season 1</b>, e recusam edição.
          {contaDeRolagens.prontas > 0 && (
            <>
              {' '}
              <b>
                {contaDeRolagens.prontas} de {contaDeRolagens.total} rolagens montadas.
              </b>
            </>
          )}{' '}
          As seis de coleta e criação são <b>as mesmas</b> que a Tier List mostra — um
          rascunho só, não duas cópias.
        </p>

        <div className="fi-grupos">
          {/* Combate é categoria, e se divide pelo lado da troca de golpes. */}
          <div className="fi-grupo">
            <span className="fi-grupo-tit">Combate</span>
            <span className="fi-sub-tit">Ofensivas</span>
            {grupoDeRolagens(COMBATE_OFENSIVAS)}
            <span className="fi-sub-tit">Defensivas</span>
            {grupoDeRolagens(COMBATE_DEFENSIVAS)}
          </div>

          <div className="fi-grupo">
            <span className="fi-grupo-tit">Coleta</span>
            {grupoDeRolagens(COLETAS)}
          </div>

          <div className="fi-grupo">
            <span className="fi-grupo-tit">Criação</span>
            {grupoDeRolagens(CRIACOES)}
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * "usa a perícia X" — a dependência do secundário, com o ícone e a cor dela.
 *
 * <p>Não é aviso de nome repetido, é declaração de origem: a fórmula da Esquiva soma o
 * nível da perícia Esquiva. Sem dizer isso, quem lê o mesmo nome nas duas seções conclui
 * que uma delas sobra — e apagar qualquer uma quebra a conta da outra.
 *
 * <p>Some quando não há perícia declarada. É o caso do Acerto, que usa <b>a perícia da
 * arma</b>: ela muda a cada arma empunhada, e apontar para uma seria mentira.
 */
function PericiaQueEntra({ id }: { id?: string }) {
  const pericia = id ? periciaPorId(id) : undefined;
  if (!pericia) return null;

  const cor = corDe(pericia);
  return (
    <em className="fi-usa-pericia" style={{ '--c': cor } as React.CSSProperties}>
      <PericiaIcone pericia={pericia.id} cor={cor} tamanho={14} />
      usa a perícia {pericia.rotulo}
    </em>
  );
}
